import { ITextFieldStyles, TextField } from '@fluentui/react';
import { Tab } from '@fluentui/react-components';
import { ArrowLeft20Regular } from '@fluentui/react-icons';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getSettings, ISettings } from '../../services/settingsService';
import AppContext from '../AppContext';
import References from './References';
import Statuses from './Statuses';
import Labels from './Labels';
import Reports from './Reports';
import OtherSettings from './OtherSettings';
import Pricing from './Pricing';
import SlaRules from './SlaRules';
import ScrollableTabList from './ScrollableTabList';
import { legacyPricingRedirect } from './pricingSettings';
import { Features, useFeatureFlag } from '@/libs/featureAccess';

const settingsTabTitle: Record<string, string> = {
  client: 'Client',
  references: 'References',
  workStatuses: 'Work Statuses',
  assetStatuses: 'Asset Statuses',
  orderStatuses: 'Order Statuses',
  labels: 'Labels',
  reports: 'Dashboards',
  pricing: 'Pricing',
  'sla-rules': 'SLA Rules',
  other: 'Global settings',
};

const Settings = () => {
  let { tab } = useParams();

  const navigate = useNavigate();
  const { search } = useLocation();

  const [clientUrl, setClientUrl] = useState('');

  useEffect(() => {
    const target =
      tab === 'pricing' ? legacyPricingRedirect(search) : undefined;
    if (target) navigate(target, { replace: true });
  }, [tab, search, navigate]);

  const context = useContext(AppContext);

  useEffect(() => {
    context.setSelectedTab(settingsTabTitle[tab ?? 'references'] ?? 'Settings');
    const abortController = new AbortController();

    getSettings(abortController).then((data: ISettings) => {
      setClientUrl(data?.clientUrl);
    });

    return () => {
      abortController.abort();
    };
  }, [tab]);

  const textFieldStyles: Partial<ITextFieldStyles> = {
    fieldGroup: { width: 300 },
    root: { padding: 10 },
  };
  const issues = useFeatureFlag(Features.FEATURE_ISSUES);
  const tabs = [
    ...(issues.isTenantReady && issues.isEnabled
      ? [{ key: 'client', label: 'Client' }]
      : []),
    { key: 'references', label: 'References' },
    { key: 'workStatuses', label: 'Work Statuses' },
    { key: 'assetStatuses', label: 'Asset Statuses' },
    { key: 'orderStatuses', label: 'Order Statuses' },
    { key: 'labels', label: 'Labels' },
    { key: 'reports', label: 'Dashboards' },
    { key: 'pricing', label: 'Pricing' },
    { key: 'sla-rules', label: 'SLA Rules' },
    { key: 'other', label: 'Global settings' },
  ];
  const activeTab = tabs.some((item) => item.key === tab)
    ? (tab as string)
    : 'references';

  return (
    <div style={{ minWidth: 0 }}>
      <ScrollableTabList
        selectedValue={activeTab}
        onTabSelect={(value) => {
          if (value === 'back') navigate('/');
          else navigate(`/settings/${value}`);
        }}
      >
        <Tab value="back" icon={<ArrowLeft20Regular />} aria-label="Back" />
        {tabs.map((item) => (
          <Tab key={item.key} value={item.key}>
            {item.label}
          </Tab>
        ))}
      </ScrollableTabList>
      {activeTab === 'client' && (
        <TextField
          label="ClientUrl"
          value={clientUrl}
          styles={textFieldStyles}
          readOnly
        />
      )}
      {activeTab === 'references' && <References />}
      {activeTab === 'workStatuses' && <Statuses entityType="work" />}
      {activeTab === 'assetStatuses' && <Statuses entityType="asset" />}
      {activeTab === 'orderStatuses' && <Statuses entityType="order" />}
      {activeTab === 'labels' && <Labels />}
      {activeTab === 'reports' && <Reports />}
      {activeTab === 'pricing' && <Pricing />}
      {activeTab === 'sla-rules' && <SlaRules />}
      {activeTab === 'other' && <OtherSettings />}
    </div>
  );
};

export default Settings;
